import { useMemo, useState } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { KpiBadge } from "@/components/design/KpiBadge";
import { CompanyLogo } from "@/pages/company/CompanyLogo";
import { companyLogoSrc, type CompanyLogoValue } from "@/pages/company/company-logo";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { LockClosedIcon, LockOpenIcon } from "./lock-icons";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  CriterionAiReason,
  CriterionKindHeading,
  CriterionRequirementFilter,
  CriterionRequirementGlyph,
  CriterionStatusDot,
  type CriterionDotTone,
  type CriterionRequirementFilterValue,
} from "./CriterionDrilldown";
import { RankingBandGroups } from "./RankingBandGroups";
import { RankingInsightsPanel } from "./RankingInsightsPanel";
import { RankingRow } from "./RankingRow";
import { RankingThresholdSlider } from "./RankingThresholdSlider";
import { RankingTopFive } from "./RankingTopFive";
import { isSectionVisible } from "./result-sections";
import { useRequirementsReferenceData } from "./use-requirements-reference-data";
import {
  DEFAULT_COMPANY_THRESHOLDS,
  buildCompanyRankingInsights,
  companyBand,
  criterionFormatter,
  type CriterionFormatter,
  type CompanyBand,
  type CompanyCriterionView,
  type CompanyRankThresholds,
  type RankedCompany,
} from "./company-ranking-derive";

/**
 * The band order, and it is the person side's (`RANK_CLUSTERS`): best first, then the two bands that
 * are not a grade at all. Same five keys on both sides — `CompanyBand` and `RankCluster` are the
 * same five strings — so a reader moving between the two lists finds the groups where they were.
 */
const COMPANY_BANDS: readonly CompanyBand[] = ["A", "B", "C", "REVIEW", "UNQUALIFIED"];

/**
 * A band's heading. A, B and C are their own name; the other two are words, and they are the PERSON
 * side's words — "Zu prüfen" and "Kriterien nicht erfüllt" name the state, not the subject, so
 * there is nothing company-specific to say and no second key to keep in step.
 */
function bandTitle(intl: IntlShape, band: CompanyBand): string {
  if (band === "REVIEW") {
    return intl.formatMessage({ id: "match.result.ranking.review", defaultMessage: "Zu prüfen" });
  }
  if (band === "UNQUALIFIED") {
    return intl.formatMessage({
      id: "match.result.ranking.unqualified",
      defaultMessage: "Kriterien nicht erfüllt",
    });
  }
  return band;
}

// THE RANKING ITSELF: one row per company, in the order genesis stored, with the evidence under it.
//
// THE ORDER IS THE SERVER'S AND THE BANDS ARE THE UI'S. `position` is a stored column, produced by
// genesis's rank rule (no disproved Muss first, then Muss fulfilment, then Kann, then pool order),
// so it is printed and never recomputed. A/B/C is the reader's own cut through that order: the
// thresholds live in this component's state, the bands are derived from them on every render, and
// nothing about them is sent anywhere. That is the whole reason the slider can exist — a stored
// band would need a write per drag and would freeze one reader's cut for everybody.
//
// NO POWERS AXIS, and no sort control that implies one. The person ranking can sort by AI
// suitability or power fit because a person has both; a company has neither, so the only axis here
// is the requirement evidence.

export interface CompanyRankingPanelProps {
  ranked: readonly RankedCompany[];
  /**
   * True when this ranking's evidence falls back to the company as it is TODAY — a saved ranking
   * stored before the run recorded its own values. It is not "the ranking is saved": a saved
   * ranking whose atoms carry values prints what was judged, and the note would then be false.
   */
  showHistoricalValueNote: boolean;
  /**
   * The logo of one company, if the caller has hydrated it.
   *
   * A PROP AND NOT A HOOK HERE. The bytes travel base64 inside the GraphQL response, so genesis
   * resolves `Company.logo` on four surfaces only and offers `companyIdentities` — ids in, name and
   * logo out, server-capped — for exactly this shape; `useCompanyLogos` drives that in rounds of 50.
   * That is a query, and this panel is the presentational half: it takes `ranked` and renders it,
   * with no Apollo client of its own. The step above owns the hydration, which also means the rounds
   * are shared with nothing else on the screen asking twice.
   *
   * Optional, defaulting to no logo: a caller that has not hydrated yet — or a test that is not
   * about logos — gets the monogram fallback `CompanyLogo` draws anyway.
   */
  logoOf?: (companyId: string) => CompanyLogoValue | null;
  hiddenSections?: readonly string[] | null;
}

// Sichtbarkeit wie im Personen-Dashboard, aus derselben Quelle (result-sections.ts). Die
// Unternehmens-Abschnitte tragen eigene Kennungen, obwohl sie in derselben Spalte landen: ein hier
// ausgeblendeter Abschnitt soll nicht zugleich einen Personen-Abschnitt verschwinden lassen.
export function CompanyRankingPanel({
  ranked,
  showHistoricalValueNote,
  logoOf,
  hiddenSections,
}: CompanyRankingPanelProps) {
  const intl = useIntl();
  const { industries } = useRequirementsReferenceData();
  // THE A/B/C CUT. Until the spark!285 review this panel held three percent states, three clamping
  // helpers and three number fields, while the candidate panel dragged the same three values on a
  // slider. One control now serves both (RankingThresholdSlider), and it owns the clamping — A above
  // B above C, one step apart — because that is a property of the control, not of either caller.
  const [thresholds, setThresholds] = useState<CompanyRankThresholds>(DEFAULT_COMPANY_THRESHOLDS);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const industryLabelById = useMemo(() => new Map(industries.map((i) => [i.value, i.label])), [industries]);
  const format = useMemo(
    () => criterionFormatter(intl, industryLabelById),
    [intl, industryLabelById],
  );

  // Bands are recomputed here, from the thresholds, so the slider needs no callback and the parent
  // never holds a band. `ranked` already carries the per-row arithmetic; only the cut moves.
  const rows = useMemo(
    () => ranked.map((row) => ({ row, band: companyBand(row, thresholds) })),
    [ranked, thresholds],
  );

  /**
   * Open or close a whole band.
   *
   * Takes the target state rather than flipping each row: flipping would turn a half-open band into
   * its own mirror image, and the button says "expand all" / "collapse all", not "invert".
   */
  function toggleAll(entryIds: readonly string[], open: boolean) {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const id of entryIds) {
        if (open) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  function toggle(entryId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }

  /**
   * TOP 5, BY THE KANN MEAN — not by an overall AI verdict.
   *
   * <p>The person side's Top 5 reads "beste Eignung" and sorts by {@code aiAssessment.suitability},
   * a judgement over the whole person. Concept v17 (c) decided there is deliberately no equivalent
   * for a company: the AI judges the expertise field and nothing else, so a whole-company score
   * would be an unchecked number sitting beside a ranking built from checked criteria. What a
   * company HAS is the score the ranking already uses, so that is what this ranks by.
   *
   * <p>Qualified only, and only rows that HAVE a score: a company whose Muss is disproved does not
   * belong in a "top" list whatever its Kann mean, and a null score means nothing could be assessed
   * — printing it as 0 % would be a claim about the company.
   */
  /**
   * MATCH ANALYSIS — the first of the three surfaces a company result owes (Kevin, 2026-09-09).
   * Built from the same rows and the SAME thresholds the bands are cut with, so the counts in the
   * box and the chips in the list can never describe different populations.
   */
  const insights = useMemo(
    () => buildCompanyRankingInsights(ranked, thresholds),
    [ranked, thresholds],
  );

  /**
   * The one recommendation, in the company side's words.
   *
   * Same four cases the person side has, deliberately — the shape comes from the shared insights —
   * but not the same sentences: the person version counts "Kandidaten" and asks for "Profilwerte"
   * to be maintained, and a company's data is neither. Only the wording differs; which case wins is
   * decided in the derive, once, for both sides.
   */
  const recommendationPercent = (value: number) =>
    intl.formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
  const recommendation = insights.recommendation;
  const recommendationContent =
    recommendation?.type === "must" ? (
      <FormattedMessage
        id="match.companyRanking.recommendMust"
        defaultMessage="{criterion} prüfen oder lockern; dieses Muss-Kriterium schließt {failed}/{total} Unternehmen aus."
        values={{
          criterion: format.requirement(recommendation.criterion.condition),
          failed: recommendation.criterion.failedCount,
          total: recommendation.criterion.total,
        }}
      />
    ) : recommendation?.type === "data" ? (
      <FormattedMessage
        id="match.companyRanking.recommendData"
        defaultMessage="Unternehmensangaben zu {criterion} nachpflegen; {missing}/{total} Werte fehlen."
        values={{
          criterion: format.requirement(recommendation.criterion.condition),
          missing: recommendation.criterion.missingActualCount,
          total: recommendation.criterion.expectedActualCount,
        }}
      />
    ) : recommendation?.type === "ai" ? (
      <FormattedMessage
        id="match.companyRanking.recommendAi"
        defaultMessage="Manually review {count} AI reasons across {companies} companies."
        values={{ count: recommendation.reasonCount, companies: recommendation.candidateCount }}
      />
    ) : recommendation?.type === "bottleneck" ? (
      <FormattedMessage
        id="match.companyRanking.recommendBottleneck"
        defaultMessage="{criterion} ist der stärkste Engpass; Ø Erfüllung {average}."
        values={{
          criterion: format.requirement(recommendation.criterion.condition),
          average: recommendationPercent(recommendation.criterion.averageFulfillment),
        }}
      />
    ) : (
      <FormattedMessage
        id="match.companyRanking.recommendNone"
        defaultMessage="Keine unmittelbare Anpassung nötig."
      />
    );

  const topScoringCompanies = useMemo(
    () =>
      ranked
        .filter(
          (row): row is RankedCompany & { score: number } =>
            row.qualification === "QUALIFIED" && row.score != null,
        )
        .sort((a, b) => b.score - a.score)
        .slice(0, 5),
    [ranked],
  );

  return (
    <div className="space-y-3">
      {insights.pool.total > 0 && isSectionVisible("companyInsights", hiddenSections) ? (
        <RankingInsightsPanel
          insights={insights}
          labelForCondition={format.requirement}
          subjectLabel={intl.formatMessage({
            id: "match.result.insights.subjectCompanies",
            defaultMessage: "Unternehmen",
          })}
          // NO `formatActual`: the box only calls it for the axis under a numeric distribution, and
          // this side reports none (company-ranking-derive, `distribution: null`).
          recommendationContent={recommendationContent}
        />
      ) : null}
      {topScoringCompanies.length > 0 && isSectionVisible("companyTopFive", hiddenSections) ? (
        <RankingTopFive
          testId="company-top-five"
          entryTestId="company-top-five-entry"
          basis={
            <FormattedMessage
              id="match.companyRanking.topFiveBasis"
              defaultMessage="Kann-Mittelwert"
            />
          }
          entries={topScoringCompanies.map((row) => ({
            key: row.entryId,
            to: `/c/${row.companyId}`,
            openLabel: intl.formatMessage(
              { id: "match.companyRanking.openCompany", defaultMessage: "{name} öffnen" },
              { name: row.name },
            ),
            name: row.name,
            score: row.score,
            scoreLabel: intl.formatMessage(
              {
                id: "match.companyRanking.topFiveScoreLabel",
                defaultMessage: "Bewertung {score} %",
              },
              { score: Math.round(row.score * 100) },
            ),
            // THE LOGO IS ALREADY HERE. This card used to draw none, on the grounds that
            // `Company.logo` travels base64 inline and five images would bloat a response that
            // already carries every criterion of every company. That reasoning predates the
            // hydration: `CompanyRankingStep` asks `companyIdentities` for the WHOLE ranking in
            // rounds of 50 and hands the lookup down as `logoOf`, and these five are a subset of
            // those rows. Reading it here costs no request at all.
            image: <CompanyTile logo={logoOf?.(row.companyId) ?? null} name={row.name} />,
          }))}
        />
      ) : null}

      {isSectionVisible("companyBands", hiddenSections) ? (
        <div className="box bg-surface p-5">
          <BoxHeader
            title={
              <span>
                {intl.formatMessage({ id: "match.result.rankingHeader", defaultMessage: "Ranking" })}
                <span className="ml-2 text-smycles-meta font-normal text-ink-muted tabular-nums">· {ranked.length}</span>
              </span>
            }
          />

          {ranked.length === 0 ? (
            <div className="bg-page box-flat py-12 text-center text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "match.companyRanking.empty",
                defaultMessage: "Der Pool dieses Matches ist leer – es gibt nichts zu ranken.",
              })}
            </div>
          ) : (
            <>
              <RankingThresholdSlider thresholds={thresholds} onChange={setThresholds} />
              <div className="mb-5">
                <p className="smycles-field-meta mt-2 text-ink-muted">
                  {intl.formatMessage({
                    id: "match.companyRanking.bandsAreDerived",
                    defaultMessage:
                      "Die Bänder A/B/C entstehen hier aus den Schwellen und werden nicht gespeichert. Die Platzierung kommt aus dem Lauf und ändert sich dabei nicht.",
                  })}
                </p>
              </div>

              {showHistoricalValueNote ? (
                <p className="smycles-field-meta mb-3 text-ink-muted">
                  {intl.formatMessage({
                    id: "match.companyRanking.historicalValueNote",
                    defaultMessage:
                      "Die Ist-Werte zeigen den heutigen Stand der Unternehmen. Bewertet wurde der Stand zum Zeitpunkt des Laufs.",
                  })}
                </p>
            ) : null}

          {/* ONE COLLAPSIBLE CARD PER BAND, in the person ranking's own shell (Kevin, 2026-09-09):
              a flat list with a letter pill per row showed the same information and read as a
              different screen. The wrapping test id stays on the region rather than moving to each
              band's list, so "the list" is still one thing to ask about. */}
          <div data-testid="company-ranking-rows">
            <RankingBandGroups
              testIdPrefix="company-ranking-cluster"
              idPrefix="company-ranking-cluster"
              groups={COMPANY_BANDS.map((band) => {
                const members = rows.filter((r) => r.band === band);
                const allOpen =
                  members.length > 0 && members.every((m) => expanded.has(m.row.entryId));
                return {
                  band,
                  title: bandTitle(intl, band),
                  count: members.length,
                  // Only the two bands whose emptiness needs explaining carry a sentence; A/B/C get
                  // the bare rule, exactly as on the person side.
                  emptyHint:
                    band === "UNQUALIFIED" ? (
                      <FormattedMessage
                        id="match.companyRanking.noMustHint"
                        defaultMessage="Hier landen Unternehmen, die ein Muss-Kriterium verfehlen oder deren Score unter der C-Schwelle liegt."
                      />
                    ) : band === "REVIEW" ? (
                      <FormattedMessage
                        id="match.companyRanking.reviewHint"
                        defaultMessage="Hier landen Unternehmen, bei denen ein Kriterium nicht bewertet werden konnte, etwa eine Fachkompetenz, die das Unternehmen nicht führt. Sie sind nicht durchgefallen, sondern brauchen einen Blick."
                      />
                    ) : undefined,
                  headerAction: (
                    <Button
                      variant="chip"
                      size="sm"
                      className="shrink-0"
                      onClick={() => toggleAll(members.map((m) => m.row.entryId), !allOpen)}
                    >
                      {allOpen ? (
                        <FormattedMessage
                          id="match.result.ranking.collapseAll"
                          defaultMessage="Alle einklappen"
                        />
                      ) : (
                        <FormattedMessage
                          id="match.result.ranking.expandAll"
                          defaultMessage="Alle aufklappen"
                        />
                      )}
                    </Button>
                  ),
                  children: (
                    <ul>
                      {members.map(({ row }) => (
                        <CompanyRankingRow
                          key={row.entryId}
                          row={row}
                          logo={logoOf?.(row.companyId) ?? null}
                          open={expanded.has(row.entryId)}
                          onToggle={() => toggle(row.entryId)}
                          format={format}
                        />
                      ))}
                    </ul>
                  ),
                };
              })}
            />
          </div>
        </>
      )}
    </div>
    ) : null}
    </div>
  );
}


/**
 * A company in the square image field of a Top-5 card.
 *
 * Not `CompanyLogo`: that primitive draws a fixed round rung (`h-10 w-10` or `h-20 w-20`) and says
 * so deliberately — "a third size is a design decision, not a call-site detail". This field is a
 * square filled edge to edge, which is a different shape rather than a third size. It reuses the
 * same source helper and the same monogram style the primitive uses, so the fallback stays one
 * decision, and it mirrors the person card, which composes its field the same way.
 */
function CompanyTile({ logo, name }: { logo: CompanyLogoValue | null; name: string }) {
  const src = companyLogoSrc(logo);
  return src ? (
    <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
  ) : (
    <div
      className="absolute inset-0 flex items-center justify-center font-serif text-smycles-display-m"
      style={AVATAR_PLACEHOLDER_STYLE}
      aria-hidden="true"
    >
      {avatarInitials(name)}
    </div>
  );
}

function CompanyRankingRow({
  row,
  logo,
  open,
  onToggle,
  format,
}: {
  row: RankedCompany;
  /** Hydrated separately from the ranking — see the panel's `useCompanyLogos` call. */
  logo: CompanyLogoValue | null;
  open: boolean;
  onToggle: () => void;
  format: CriterionFormatter;
}) {
  const intl = useIntl();
  /**
   * Which half of the evidence the row is showing. Lives here rather than in the breakdown because
   * the two pills in the header select it — and it survives closing and reopening the row, so a
   * reader who came back to the Muss half finds it where they left it.
   */
  const [requirement, setRequirement] = useState<CriterionRequirementFilterValue>("all");

  /**
   * Open the row already narrowed to one half — or, on the pill that is already showing, close it
   * again. Clicking the OTHER pill of an open row switches halves instead of closing, which is what
   * a reader means by clicking it.
   */
  function onOpenRequirement(next: CriterionRequirementFilterValue) {
    if (open && requirement === next) {
      onToggle();
      return;
    }
    setRequirement(next);
    if (!open) onToggle();
  }
  /**
   * THE SUM OF FULFILMENTS over the assessable Kann criteria — not a count of met ones — so that the
   * pill's fraction IS the score. The person pill does the same, and for the same reason: a count
   * cannot express a graded value. Since concept v17 a company criterion is binary, so for anything
   * this build produces the two are identical; the difference shows on a ranking STORED before v17,
   * whose expertise criterion can carry a 0.6, and there a met-count would print 0/1 beside a score
   * of 60 %.
   *
   * Coverage — how many of the stated Kann criteria could be judged at all — is a separate fact and
   * lives in the Rechenweg, exactly as it does on the person side.
   */
  const optionalSum = row.criteria.reduce(
    (sum, criterion) =>
      criterion.isMust || !criterion.assessable ? sum : sum + (criterion.fulfillment ?? 0),
    0,
  );
  const notAssessable = intl.formatMessage({
    id: "match.companyRanking.notAssessable",
    defaultMessage: "nicht bewertbar",
  });
  /**
   * THE RECHENWEG, in the same words the person row uses for it.
   *
   * Not the person renderer itself: that one groups the Kann criteria by CRITERION KIND
   * (`criterionKindLabel`, a person-side taxonomy of Erfahrung/Fachkompetenz/Powers/…) and a company
   * has no such kinds — it has three criteria types, and grouping two of them under one head would
   * invent a category. So this lists the criteria themselves, met over assessable, which is exactly
   * what the mean above it is taken over.
   */
  const scoreInfo = (
    <div className="space-y-2">
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreFormula"
          defaultMessage="Score = Durchschnitt der Kann-Kriterien."
        />
      </div>
      {/* BY CRITERION KIND, not one line per criterion (Kevin, 2026-09-10: the threshold and the
          particular expertise wording are not needed here). That is also what the person Rechenweg
          does, and it removes an ambiguity this side had: two expertise criteria both printed
          „Fachkompetenz: …" and were told apart only by their detail.

          Summed over the ASSESSABLE ones, so the fractions add up to the score above. A kind whose
          criteria could none of them be judged does not appear — the coverage line below is what
          accounts for those, rather than a row reading 0/0. */}
      <div className="space-y-1">
        {(() => {
          const groups: { label: string; order: number; sum: number; count: number }[] = [];
          for (const criterion of row.criteria) {
            if (criterion.isMust || !criterion.assessable) continue;
            const { label, order } = format.kind(criterion.condition);
            let group = groups.find((g) => g.label === label);
            if (!group) {
              group = { label, order, sum: 0, count: 0 };
              groups.push(group);
            }
            group.sum += criterion.fulfillment ?? 0;
            group.count += 1;
          }
          groups.sort((left, right) => left.order - right.order);
          return groups.map((group) => (
            <div key={group.label} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate">{group.label}</span>
              <span className="shrink-0 tabular-nums">
                {intl.formatNumber(group.sum, { maximumFractionDigits: 1 })}/{group.count}
              </span>
            </div>
          ));
        })()}
      </div>
      <div className="pt-2 tabular-nums">
        {row.score == null ? (
          <FormattedMessage
            id="match.result.ranking.scoreNotAssessable"
            defaultMessage="Kann-Kriterien: nicht bewertbar"
          />
        ) : (
          <FormattedMessage
            id="match.result.ranking.scoreCriteriaValue"
            defaultMessage="Kann-Kriterien: {criteria}"
            values={{
              criteria: intl.formatNumber(row.score, {
                style: "percent",
                maximumFractionDigits: 0,
              }),
            }}
          />
        )}
      </div>
      {/* How much the number rests on: a mean over 1 of 3 criteria is not the same claim as the
          same mean over 3 of 3. Never multiplied into the score — it is a separate fact. */}
      {row.optionalTotal > row.optionalAssessed ? (
        <div className="tabular-nums text-ink-muted">
          <FormattedMessage
            id="match.result.ranking.scoreCoverage"
            defaultMessage="{assessable} von {defined} Kriterien bewertbar"
            values={{ assessable: row.optionalAssessed, defined: row.optionalTotal }}
          />
        </div>
      ) : null}
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreMustGate"
          defaultMessage="Muss-Kriterien bleiben zusätzlich ein hartes Gate."
        />
      </div>
    </div>
  );
  const meta = [
    // Counted and named, never folded into the score: a criterion nothing could assess is a gap in
    // the evidence, and a reader who cannot see how big it is cannot judge the row.
    row.unassessable > 0
      ? intl.formatMessage(
          {
            id: "match.companyRanking.unassessableMeta",
            defaultMessage:
              "{count, plural, one {# Kriterium nicht bewertbar} other {# Kriterien nicht bewertbar}}",
          },
          { count: row.unassessable },
        )
      : null,
    row.inactive
      ? intl.formatMessage({ id: "match.companyPool.inactive", defaultMessage: "inaktiv" })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // The row geometry, the hover and the fixed name column come from RankingRow, the same
    // component the person ranking renders. What stays here is the subject: the logo, the name,
    // the two pills and the drill-down.
    <RankingRow
      open={open}
      onToggle={onToggle}
      id={`company-ranking-row-${row.companyId}`}
      toggleId={`company-ranking-${row.companyId}-toggle`}
      testId="company-ranking-row"
      name={row.name}
      toggleLabel={intl.formatMessage(
        { id: "match.result.ranking.toggleEvidence", defaultMessage: "Nachweise für {name} anzeigen" },
        { name: row.name },
      )}
      meta={meta || undefined}
      bubble={
        /* NO POSITION NUMBER (Kevin, 2026-09-09): the Top 5 above already says who came out best, and
           a rank printed beside every row of a band-major list invites reading it as the band's own
           order. The stored position still governs the order WITHIN a band; it is simply not
           printed. Its aria-label went with it — a number nobody shows needs no name. */
        /* THE LOGO IS THE ONLY LINK OUT. Clicking anywhere else opens the evidence, which is what
           the person row does with its name. NOTHING from this ranking travels the other way: the
           company page shows no match, no position and no band (the company page) — a
           ranking is one user's private reading of their own pool, not a property of the company. */
        <Link
          to={`/c/${row.companyId}`}
          onClick={(event) => event.stopPropagation()}
          title={intl.formatMessage(
            { id: "match.companyRanking.openCompany", defaultMessage: "{name} öffnen" },
            { name: row.name },
          )}
          className="block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
        >
          <CompanyLogo logo={logo} name={row.name} size="row" />
        </Link>
      }
      badges={
        <>
        {/* The person row's own pills, and they replace the "Nachweis" button rather than sitting
            next to it: both of them open the same drill-down, so the evidence is still one click
            away, and the numbers that were prose in the meta line are now where the reader of a
            person ranking already looks for them.
            EACH PILL SELECTS ITS OWN HALF of the evidence, which is why only the one that was
            clicked is filled (Kevin, 2026-09-09: "only the button i click on should turn black").
            They were both bound to `open`, so opening the row lit both — and said, wrongly, that
            both halves were being shown. */}
        <KpiBadge
          id={`company-ranking-${row.companyId}-must`}
          tone={row.mustTotal > 0 ? "neutral" : "muted"}
          active={open && requirement === "must"}
          expanded={open && requirement === "must"}
          // stopPropagation, seit die ganze Zeile die Klickflaeche ist (RankingRow): ohne das
          // oeffnet die Pille ihre Haelfte und der durchgereichte Klick schliesst die Zeile
          // sofort wieder. Dieselbe Vorkehrung tragen die Badges der Personenzeile.
          onClick={(event) => {
            event.stopPropagation();
            onOpenRequirement("must");
          }}
          title={intl.formatMessage({
            id: "match.companyRanking.must",
            defaultMessage: "Muss",
          })}
        >
          <LockClosedIcon />
          <FormattedMessage id="match.companyRanking.must" defaultMessage="Muss" />
          <span>
            {row.mustMet}/{row.mustTotal}
          </span>
        </KpiBadge>
        <KpiBadge
          id={`company-ranking-${row.companyId}-kann`}
          tone={row.optionalTotal > 0 ? "neutral" : "muted"}
          active={open && requirement === "kann"}
          expanded={open && requirement === "kann"}
          // stopPropagation, seit die ganze Zeile die Klickflaeche ist (RankingRow): ohne das
          // oeffnet die Pille ihre Haelfte und der durchgereichte Klick schliesst die Zeile
          // sofort wieder. Dieselbe Vorkehrung tragen die Badges der Personenzeile.
          onClick={(event) => {
            event.stopPropagation();
            onOpenRequirement("kann");
          }}
          title={intl.formatMessage({
            id: "match.companyRanking.optional",
            defaultMessage: "Kann",
          })}
          info={{
            title: intl.formatMessage({
              id: "match.result.ranking.scoreInfoTitle",
              defaultMessage: "Rechenweg",
            }),
            ariaLabel: intl.formatMessage({
              id: "match.result.ranking.scoreInfoAria",
              defaultMessage: "Rechenweg anzeigen",
            }),
            content: scoreInfo,
          }}
        >
          <LockOpenIcon />
          <FormattedMessage id="match.companyRanking.optional" defaultMessage="Kann" />
          {/* ASSESSED over total, never "0/0" for a row whose Kann criteria exist but could none of
              them be judged — that reads as "missed everything" (the person side's spark#175). */}
          <span>
            {row.optionalTotal > 0 && row.optionalAssessed === 0
              ? notAssessable
              : `${intl.formatNumber(optionalSum, { maximumFractionDigits: 1 })}/${row.optionalAssessed}`}
          </span>
        </KpiBadge>
        </>
      }
    >
      {/* No `open ?` guard around this any more: RankingRow keeps the drill-down mounted and inert
          while closed, which is what lets the open and the close both animate. The person row has
          worked that way all along. */}
      <div id={`company-ranking-${row.companyId}-evidence`} className="mt-3 border-t border-ink/10 pt-3">
        {row.criteria.length === 0 ? (
          <p className="smycles-field-meta text-ink-muted">
            {intl.formatMessage({
              id: "match.companyRanking.noCriteria",
              defaultMessage: "Für dieses Match sind keine Unternehmens-Anforderungen gesetzt.",
            })}
          </p>
        ) : (
          <CompanyCriteriaBreakdown
            row={row}
            format={format}
            requirement={requirement}
            onRequirementChange={setRequirement}
          />
        )}
      </div>
    </RankingRow>
  );
}

// THE SHORTLIST TICK. Drawn here, on the ranking row, because this is where the reader is when they
// decide — and it writes the POOL row, which is why genesis gates it on the pool right (discover

/**
 * THE THREE OUTCOME BUCKETS of the company drill-down.
 *
 * Three and not the person side's four: there is no „kulant erfüllt" here, because a company
 * criterion has no level to be lenient about since concept v17 made expertise binary. And the third
 * one is the bucket the person side does not need at all — see {@link criterionOutcome}.
 */
type CriterionOutcome = "fulfilled" | "unfulfilled" | "notAssessable";

/**
 * WHICH BUCKET A CRITERION FALLS INTO — and the one rule of this whole surface, applied first.
 *
 * A criterion nothing could assess gets its OWN bucket, its own count and its own word. It must
 * never fall into „nicht erfüllt": that would turn a gap in the evidence into a verdict against the
 * company, which is the single most-repeated ratified rule here (the company matching model, Punkt 6;
 * Konzept v15).
 *
 * `assessable` is read BEFORE the classification is looked at at all, and the UNKNOWN
 * classification is read as the same answer — genesis states "nothing could assess this" twice and
 * either signal alone is enough, exactly as {@code parseCompanyResult} reads it. Everything after
 * that is decided by the CLASSIFICATION and never by the fulfilment number, so this function and
 * {@link verdictWord} read the same field in the same order and cannot disagree: no row can be
 * filed under one outcome while its line prints another.
 *
 * PARTIAL is not fulfilled, so it sits with the unfulfilled ones — and says „teilweise erfüllt" on
 * its own line, which is why the bucket is not a verdict. It is unreachable for a ranking produced
 * since concept v17 and kept for the ones stored before it.
 */
function criterionOutcome(criterion: CompanyCriterionView): CriterionOutcome {
  if (!criterion.assessable || criterion.classification === "UNKNOWN") return "notAssessable";
  return criterion.classification === "SATISFIED" ? "fulfilled" : "unfulfilled";
}

/**
 * The dot's colour, in the person ranking's own three tones (`CriterionDrilldown`).
 *
 * Amber covers both things a reader has to look at — nothing could assess it, or a stored pre-v17
 * grade landed between 0 and 1 — and rose is reserved for the one case the run actually disproved.
 * Derived from {@link criterionOutcome} rather than re-testing `assessable`, so the rule above is
 * decided in exactly one place.
 */
function criterionTone(criterion: CompanyCriterionView): CriterionDotTone {
  const outcome = criterionOutcome(criterion);
  if (outcome === "fulfilled") return "met";
  if (outcome === "notAssessable" || criterion.classification === "PARTIAL") return "attention";
  return "unmet";
}

/**
 * THE DRILL-DOWN OF ONE ROW: two filters, then the criteria grouped by kind.
 *
 * The person ranking's shape (Kevin, 2026-09-09, comparing the two screens): a requirement axis and
 * an outcome axis as two segmented controls, and under them one collapsible group per criterion
 * kind. The pieces whose look has to match are imported from `CriterionDrilldown`; what stays here
 * is which bucket a criterion belongs in and what the buckets are called — see
 * {@link criterionOutcome}.
 *
 * THE REQUIREMENT AXIS IS CONTROLLED BY THE ROW, because the row's two pills select it: clicking
 * „Muss" opens the evidence already narrowed to the Muss criteria, and only that pill fills. The
 * outcome axis and the collapsed kinds stay local — nothing outside this body selects those.
 *
 * THE REST OF THE STATE IS THIS COMPONENT'S, not the panel's, and that is the one difference from
 * the person side. There the same three values live in the panel keyed by person id, because the
 * row's markup is inline and has nowhere else to put them; here the row is a component, so the
 * filters belong to it. Same behaviour — the filters survive closing and reopening a row, because
 * the row itself stays mounted and only this body unmounts.
 */
function CompanyCriteriaBreakdown({
  row,
  format,
  requirement,
  onRequirementChange,
}: {
  row: RankedCompany;
  format: CriterionFormatter;
  requirement: CriterionRequirementFilterValue;
  onRequirementChange: (next: CriterionRequirementFilterValue) => void;
}) {
  const intl = useIntl();
  // null = "the reader has not chosen yet", which is NOT the same as any of the three buckets: it
  // is what lets the default below follow the row instead of freezing on one word.
  const [outcome, setOutcome] = useState<CriterionOutcome | null>(null);
  const [collapsedKinds, setCollapsedKinds] = useState<ReadonlySet<string>>(new Set());

  const counts: Record<CriterionOutcome, number> = { fulfilled: 0, unfulfilled: 0, notAssessable: 0 };
  const classified = row.criteria.map((criterion, originalIndex) => {
    const bucket = criterionOutcome(criterion);
    counts[bucket] += 1;
    return { criterion, bucket, originalIndex };
  });

  /**
   * WHICH BUCKET OPENS FIRST: the best-news one that has anything in it.
   *
   * The person side opens on "Erfüllt" unconditionally and shows an explaining hint when that is
   * empty. Here the first non-empty bucket wins instead, because a company row is opened to read
   * its evidence and a first screen that says "nothing here" of a row that HAS evidence would make
   * the reader work the controls before they can see any. The caller has already ruled out the
   * no-criteria case, so this chain always lands somewhere non-empty.
   */
  const activeOutcome: CriterionOutcome =
    outcome ??
    (counts.fulfilled > 0 ? "fulfilled" : counts.unfulfilled > 0 ? "unfulfilled" : "notAssessable");

  const inOutcome = classified.filter((entry) => entry.bucket === activeOutcome);
  // Counted INSIDE the selected bucket, as on the person side: the number then answers "how many of
  // these are Muss?" rather than restating the row's own pills, which count the whole row.
  const mustCount = inOutcome.filter((entry) => entry.criterion.isMust).length;
  const kannCount = inOutcome.length - mustCount;
  const shown = inOutcome.filter((entry) =>
    requirement === "all"
      ? true
      : requirement === "must"
        ? entry.criterion.isMust
        : !entry.criterion.isMust,
  );

  // Grouped in the run's own order WITHIN a group — the same order the Rechenweg lists them in, so
  // the two readings of one row cannot be read as two different rows. Only the GROUPS are sorted,
  // and by the editor's order rather than alphabetically, which would depend on the locale.
  const groups: { label: string; order: number; items: typeof shown }[] = [];
  for (const entry of shown) {
    const kind = format.kind(entry.criterion.condition);
    const existing = groups.find((group) => group.label === kind.label);
    if (existing) existing.items.push(entry);
    else groups.push({ label: kind.label, order: kind.order, items: [entry] });
  }
  groups.sort((left, right) => left.order - right.order);

  const notAssessableWord = intl.formatMessage({
    id: "match.companyRanking.notAssessable",
    defaultMessage: "nicht bewertbar",
  });
  // „Erfüllt" and „Nicht erfüllt" are the PERSON ranking's own two words, reused rather than
  // restated: they name the state and not the subject, so there is nothing company-specific to say
  // and no second key to keep in step. The third bucket is the one that needs its own key, because
  // the ratified company word for it is „nicht bewertbar" where the person side says „Zu prüfen".
  const outcomeName: Record<CriterionOutcome, string> = {
    fulfilled: intl.formatMessage({
      id: "match.result.ranking.tabNameFulfilled",
      defaultMessage: "Erfüllt",
    }),
    unfulfilled: intl.formatMessage({
      id: "match.result.ranking.tabNameUnfulfilled",
      defaultMessage: "Nicht erfüllt",
    }),
    notAssessable: notAssessableWord,
  };
  // A requirement filter that hid everything is a different sentence from a bucket that is empty,
  // and only the first one has a way out worth offering.
  const filterIsTheCulprit = requirement !== "all" && inOutcome.length > 0;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 pb-3">
        <CriterionRequirementFilter
          testId="company-criteria-requirement"
          value={requirement}
          mustCount={mustCount}
          kannCount={kannCount}
          onChange={onRequirementChange}
        />
        <SegmentedControl<CriterionOutcome>
          size="kpi"
          id={`company-ranking-${row.companyId}-outcome`}
          data-testid="company-criteria-outcome"
          value={activeOutcome}
          onChange={setOutcome}
          options={[
            {
              value: "fulfilled",
              label: intl.formatMessage(
                { id: "match.result.ranking.fulfilledTab", defaultMessage: "Erfüllt ({count})" },
                { count: counts.fulfilled },
              ),
            },
            {
              value: "unfulfilled",
              label: intl.formatMessage(
                {
                  id: "match.result.ranking.unfulfilledTab",
                  defaultMessage: "Nicht erfüllt ({count})",
                },
                { count: counts.unfulfilled },
              ),
            },
            // THE THIRD BUCKET, and it is offered only when it holds something — the person side's
            // rule for its own conditional tabs, and here it also means an unassessable criterion
            // can never be silently folded into either of the other two: if the bucket is missing,
            // it is because there is nothing in it.
            ...(counts.notAssessable > 0
              ? [
                  {
                    value: "notAssessable" as const,
                    label: intl.formatMessage(
                      {
                        id: "match.companyRanking.notAssessableTab",
                        defaultMessage: "Nicht bewertbar ({count})",
                      },
                      { count: counts.notAssessable },
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>
      {groups.length === 0 ? (
        // Neutral, as every state signal on this surface is: one sentence and, where there is one,
        // a way back. No accent block and no coloured rule — those are the person panel's own
        // flourish and Kevin has rejected them as state signals here more than once.
        <div id={`company-ranking-${row.companyId}-empty`} className="smycles-field-meta flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-muted" data-testid="company-criteria-empty">
          <span>
            {filterIsTheCulprit ? (
              <FormattedMessage
                id="match.result.ranking.emptyFilteredTab"
                defaultMessage="Keine {requirement}-Kriterien unter „{tab}“."
                values={{
                  requirement:
                    requirement === "must"
                      ? intl.formatMessage({ id: "match.companyRanking.must", defaultMessage: "Muss" })
                      : intl.formatMessage({
                          id: "match.companyRanking.optional",
                          defaultMessage: "Kann",
                        }),
                  tab: outcomeName[activeOutcome],
                }}
              />
            ) : (
              <FormattedMessage
                id="match.result.ranking.emptyTab"
                defaultMessage="Unter „{tab}“ gibt es keine Kriterien."
                values={{ tab: outcomeName[activeOutcome] }}
              />
            )}
          </span>
          {filterIsTheCulprit ? (
            <Button id={`company-ranking-${row.companyId}-show-all`} variant="ghost" size="sm" onClick={() => onRequirementChange("all")}>
              <FormattedMessage
                id="match.result.ranking.emptyShowAll"
                defaultMessage="Alle Kriterien zeigen"
              />
            </Button>
          ) : null}
        </div>
      ) : (
        groups.map((group) => {
          const kindOpen = !collapsedKinds.has(group.label);
          return (
            <div key={group.label} className="mb-3 last:mb-0" data-testid="company-criteria-kind">
              <CriterionKindHeading
                label={group.label}
                count={group.items.length}
                open={kindOpen}
                onToggle={() =>
                  setCollapsedKinds((prev) => {
                    const next = new Set(prev);
                    if (next.has(group.label)) next.delete(group.label);
                    else next.add(group.label);
                    return next;
                  })
                }
              />
              {kindOpen ? (
                <ul className="mt-1 gap-x-6 @3xl:columns-2 @7xl:columns-3">
                  {group.items.map(({ criterion, bucket, originalIndex }) => (
                    <CriterionLine
                      key={`${row.entryId}-${originalIndex}`}
                      criterion={criterion}
                      outcome={bucket}
                      row={row}
                      format={format}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })
      )}
    </>
  );
}

function CriterionLine({
  criterion,
  outcome,
  row,
  format,
}: {
  criterion: CompanyCriterionView;
  outcome: CriterionOutcome;
  row: RankedCompany;
  format: CriterionFormatter;
}) {
  const intl = useIntl();
  const [aiReasonOpen, setAiReasonOpen] = useState(false);
  const aiReason = criterion.aiReason?.trim();
  const requirementWord = criterion.isMust
    ? intl.formatMessage({ id: "match.companyRanking.must", defaultMessage: "Muss" })
    : intl.formatMessage({ id: "match.companyRanking.optional", defaultMessage: "Kann" });
  /**
   * THE VERDICT IS KEPT, and it is also the dot's tooltip.
   *
   * Kept because the dot alone cannot say everything this line has to: amber covers „nicht
   * bewertbar" AND „teilweise erfüllt", which are two different statements about a company, and a
   * colour is unreadable to anyone who is not hovering it with a working eye for hue.
   *
   * SO WHY IS IT NOT PRINTED ANY MORE? Kevin, 2026-09-10: the outcome filter has no „Alle" — exactly
   * one bucket is shown at a time — so every visible line already carries its verdict in the
   * selected tab („Erfüllt (3)", „Nicht bewertbar (1)"). Repeating it per row said the same thing
   * three times over.
   *
   * The word itself does NOT leave the surface, which is what the ratified rule is about: it is the
   * dot's title here, and the tab's label above. Same string for both, so they cannot drift.
   */
  const verdict = verdictWord(intl, criterion);
  return (
    // `data-outcome` is the bucket this line was FILED under, handed down rather than recomputed:
    // a test can then prove that the filing and the word on the dot agree.
    <li className="break-inside-avoid py-1.5" data-testid="company-criterion-line" data-outcome={outcome}>
      <div className="smycles-field-value grid min-w-0 grid-cols-[0.5rem_1.5rem_minmax(0,1fr)] items-start gap-2">
        <CriterionStatusDot tone={criterionTone(criterion)} title={verdict} />
        {/* THE VERDICT, FOR ANYONE NOT READING THE COLOUR. The dot is `aria-hidden` and decorative —
            right on the person side, which prints the word beside it, but here the word is no longer
            printed, so without this the verdict would be missing from the accessibility tree and a
            line read on its own would carry no outcome at all. Visually hidden, not removed. */}
        <span className="sr-only">{verdict}</span>
        <CriterionRequirementGlyph isMust={criterion.isMust} label={requirementWord} />
        <div className="min-w-0 space-y-1">
          <div className="text-smycles-meta text-ink">{format.requirement(criterion.condition)}</div>
          <div className="text-smycles-label text-ink-muted">
            {intl.formatMessage(
              { id: "match.companyRanking.actualValue", defaultMessage: "Ist: {value}" },
              { value: format.actual(criterion, row) },
            )}
          </div>
          {aiReason ? (
            <CriterionAiReason
              reason={aiReason}
              open={aiReasonOpen}
              onToggle={() => setAiReasonOpen((open) => !open)}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * The verdict, in words.
 *
 * A criterion nothing could assess reads "nicht bewertbar" and NEVER "nicht erfüllt" — the one rule
 * that must survive every edit to this file. `assessable` is false exactly then, so it is checked
 * before the classification is looked at at all.
 */
function verdictWord(intl: IntlShape, criterion: CompanyCriterionView): string {
  if (!criterion.assessable) {
    return intl.formatMessage({
      id: "match.companyRanking.notAssessable",
      defaultMessage: "nicht bewertbar",
    });
  }
  switch (criterion.classification) {
    case "SATISFIED":
      return intl.formatMessage({ id: "match.companyRanking.verdictMet", defaultMessage: "erfüllt" });
    // Unreachable for a ranking generated since concept v17 — company criteria are binary now — and
    // kept for the ones stored before it, which must still open. See ClassificationSchema.
    case "PARTIAL":
      return intl.formatMessage({
        id: "match.companyRanking.verdictPartial",
        defaultMessage: "teilweise erfüllt",
      });
    case "VIOLATED":
      return intl.formatMessage({
        id: "match.companyRanking.verdictUnmet",
        defaultMessage: "nicht erfüllt",
      });
    // UNKNOWN cannot arrive here (assessable is false for it), and the arm exists so that the
    // switch stays total rather than falling through to a verdict it has no right to give.
    //
    // „nicht bewertbar" is the RATIFIED label for this outcome (the company matching model, Punkt 6 der
    // offenen Liste, Konzept v15 vom 2026-09-07). Do NOT "align" it to the concept's prose word
    // „beurteilbar": that word is internal to the document and explicitly not surface text, while
    // this one is what the person ranking has shown for months in seven further strings. One
    // concept, one word, both rankings.
    case "UNKNOWN":
    default:
      return intl.formatMessage({
        id: "match.companyRanking.notAssessable",
        defaultMessage: "nicht bewertbar",
      });
  }
}
